import {useCallback, useMemo} from 'react';
import {useUrl} from '@shopify/hydrogen';

interface UseSocialShareType {
  facebookShareUrl: string;
  twitterShareUrl: string;
  linkedinShareUrl: string;
  emailShareUrl: string;
  onShare: (url: string) => void;
}

export default function useSocialShare(
  relativeHref: string,
  title?: string,
): UseSocialShareType {
  const {origin}: URL = useUrl();
  const encodedUrl = useMemo(
    () => encodeURIComponent(`${origin}${relativeHref}`),
    [origin, relativeHref],
  );

  const encodedTitle = useMemo(() => encodeURIComponent(title), [title]);

  const facebookShareUrl = useMemo(
    () =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&p[title]=${encodedTitle}`,
    [encodedUrl, encodedTitle],
  );

  const twitterShareUrl = useMemo(
    () =>
      `https://twitter.com/share?url=${encodedUrl}&ref_src=twsrc&text=${encodedTitle}`,
    [encodedUrl, encodedTitle],
  );

  const linkedinShareUrl = useMemo(
    () =>
      `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
    [encodedUrl, encodedTitle],
  );

  const emailShareUrl = useMemo(
    () => `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
    [encodedUrl, encodedTitle],
  );

  const onShare = useCallback((url: string): void => {
    if (url) window.open(url, '_blank');
  }, []);

  return {
    facebookShareUrl,
    twitterShareUrl,
    linkedinShareUrl,
    emailShareUrl,
    onShare,
  };
}
